export type AnimChannel = 0 | 1 | 2; // 0 = rotX, 1 = rotY, 2 = rotZ

export interface AnimTrack {
  bone: number;
  channel: AnimChannel;
  times: Float32Array;
  values: Float32Array;
}

export interface Animation {
  duration: number;
  tracks: AnimTrack[];
}

export const makeAnimTrack = (
  bone: number,
  channel: AnimChannel,
  keyframes: ReadonlyArray<readonly [number, number]>,
): AnimTrack => {
  const n = keyframes.length;
  if (n === 0) throw new Error('animation track requires at least one keyframe');
  const times = new Float32Array(n);
  const values = new Float32Array(n);
  for (let i = 0; i < n; i++) {
    times[i] = keyframes[i]![0];
    values[i] = keyframes[i]![1];
  }
  return { bone, channel, times, values };
};

const sampleTrack = (track: AnimTrack, t: number): number => {
  const times = track.times;
  const values = track.values;
  const n = times.length;
  if (n === 1 || t <= times[0]!) return values[0]!;
  if (t >= times[n - 1]!) return values[n - 1]!;
  for (let i = 0; i < n - 1; i++) {
    const t0 = times[i]!;
    const t1 = times[i + 1]!;
    if (t >= t0 && t <= t1) {
      const u = t1 === t0 ? 0 : (t - t0) / (t1 - t0);
      return values[i]! + (values[i + 1]! - values[i]!) * u;
    }
  }
  return 0;
};

export const sampleAnim = (anim: Animation, t: number, eulers: Float32Array): void => {
  const d = anim.duration;
  const tw = d > 0 ? ((t % d) + d) % d : 0;
  for (let i = 0; i < anim.tracks.length; i++) {
    const track = anim.tracks[i]!;
    const v = sampleTrack(track, tw);
    eulers[track.bone * 3 + track.channel] = v;
  }
};
